import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/useStores';
import { useController } from '../../hooks/useController';
import NavigationBlocker from '../../utils/NavigationBlocker';
import ColumnResizer from '../ColumnResizer';
import { keyboard } from '../../lib/shortcuts';
import ModelExplorer from '../ModelExplorer';
import EdgPanel from '../../diagram/EdgPanel';
import OET from '../OET';
import FsmPanel from '../../diagram/FsmPanel';
import InspectorComponent from '../Inspector';
import Modal from '../Modal';
import AddObjectForm from './AddObjectForm';
import ReportView from '../ReportView';
import AddEventForm from './AddEventForm';
import AddFSMForm from './AddFSMForm';
import AddMethodForm from './AddMethodForm';
import AddStateForm from './AddStateForm';
import AddTransitionModal from './AddTransitionModal';
import BusyModal from './BusyModal';
import Toolbar from '../Toolbar';
import KeyboardShortcuts from '../KeyboardShortcuts';
import { Undo, Redo, Keyboard } from '../Icons';

import AddDependencyController from '../../merode/controllers/AddDependencyController';
import DeleteAnyController from '../../merode/controllers/DeleteAnyController';
import EditFSMController from '../../merode/controllers/EditFSMController';

import confirm from './confirmation';
import AddInheritanceForm from './AddInheritanceForm';
import { DeleteInheritanceForm, DeleteObjectForm } from './DeleteElementForm';

import type { IModelVersion } from '../../stores/projects/model_version';
import type { IModelVersion as IControllerModelVersion } from '../../merode/controllers/Controller';
import type { IModel } from '../../stores/model';
import type { IObjectType } from '../../stores/model/metamodel/object_type';
import type { IFSM } from '../../stores/model/metamodel/fsm';
import type { Position, StateType, DependencyType } from '../../merode/commands/types';
import type { Notation } from '../../diagram/ObjectTypeNode';
import Report from '../../merode/checks/Report';
import { MessageType } from '../../merode/checks/Message';

// The render passes a vestigial `id` prop to the Inspector (carried over
// verbatim from the legacy JSX); the committed Inspector destructures only its
// declared props and ignores it. Re-type the component at the boundary to accept
// the optional `id` so the byte-identical JSX still type-checks — runtime
// component is unchanged.
const Inspector = InspectorComponent as React.FC<
  React.ComponentProps<typeof InspectorComponent> & { id?: string }
>;

function unexpectedErrorReport(error: unknown): Report {
  const report = new Report();
  const message =
    error instanceof Error && error.message
      ? error.message
      : 'Unexpected error while updating the model.';
  report.addMessage(MessageType.ERROR, message);
  return report;
}

/** Props for {@link ProjectVersion}. */
interface ProjectVersionProps {
  /** The ModelVersion to show. */
  version: IModelVersion;
  /** The notation to use (MERODE/UML). */
  notation: Notation;
}

// --- Modal-group state shapes (one per `useState` group; see the grouping note
// on the component). The shapes mirror the props each group is spread/passed
// into, so the render sites type without per-call casts. The handlers receive
// element *ids* from the panels (e.g. `fsm.objectType.id`); the forms declare
// the matching props as the concrete `IObjectType`/`IFSM` interfaces and re-cast
// the id back to a `string` at their own controller-construction boundary (same
// pattern as the committed forms). So the id→interface cast lives at the setState
// site here, runtime-neutral (the same id string flows through). ---

interface AddObjectState {
  addObjectModalOpen: boolean;
  newObjectPosition: Position | null;
}

interface InheritanceProperties {
  supertype: string;
  subtype: string;
}

interface AddInheritanceState {
  addInheritanceModalOpen: boolean;
  newInheritanceProperties: InheritanceProperties | null;
}

interface SpecialisedEventProperties {
  ownerEventType: string;
}

interface AddSpecialisedEventState {
  addSpecialisedEventModalOpen: boolean;
  newSpecialisedEventProperties: SpecialisedEventProperties | null;
}

interface AddMethodState {
  addMethodModalOpen: boolean;
  newMethodEventType: string | null;
  newMethodObjectType: string | null;
}

interface AddFSMState {
  addFSMModalOpen: boolean;
  newFSMObjectType: string | null;
  newFSMCopyFrom: string | null;
  newFSMCalculate: boolean;
}

interface StateProperties {
  type: StateType | null;
  position: Position | null;
  objectType: IObjectType;
  fsm: IFSM;
}

interface AddStateState {
  addStateModalOpen: boolean;
  newStateProperties: StateProperties | null;
}

interface TransitionProperties {
  objectType: IObjectType;
  fsm: IFSM;
  source: string | null;
  target: string | null;
}

interface AddTransitionState {
  addTransitionModalOpen: boolean;
  newTransitionProperties: TransitionProperties | null;
}

interface DeleteElementModalProps {
  id: string;
  type: 'Object' | 'Inheritance';
}

interface DeleteElementState {
  deleteElementModalOpen: boolean;
  deleteElementModalProps: DeleteElementModalProps | null;
}

/** A confirmable wrapper factory: wraps a handler behind the dirty-inspector
 * guard, returning a same-arity caller. The wrapped handlers take varied arg
 * shapes, so the factory is generic over the argument tuple. */
type Confirmable = <A extends unknown[]>(
  action: string,
  handler: (...args: A) => void
) => (...args: A) => void;

/**
 * Main view for project/model version
 *
 * Renders the following subviews:
 * - ModelExplorer
 * - EDG
 * - OET
 * - FSMs
 * - Inspector
 * - A floating toolbar for undo/redo
 *
 * And the following modal views:
 * - modal with an AddObjectForm
 * - modal with an AddEventForm
 * - modal with an AddMethodForm
 * - modal with a ReportView
 * - modal with a KeyboardShortcuts component
 * - modal with an AddFSMForm
 * - modal with an AddStateForm
 * - an AddTransitionModal
 * - modal with an AddInheritanceForm
 *
 * Props:
 * - version: the ModelVersion to show
 * - notation: the notation to use (MERODE/UML)
 *
 * The `ui` store was injected with mobx-react (`inject(({ store }) => ({ ui:
 * store.ui }))`); it now comes from useStores().
 *
 * State grouping note: the original class used a single `this.state` object with
 * partial `setState` merges. Every `setState` call here only ever wrote the full
 * key-set of one logical modal group atomically, so each group becomes its own
 * `useState` object. This preserves the class's merge semantics exactly: state
 * keys in other groups are untouched by a group's update, and a setter that
 * replaces a group's whole object matches what the class wrote for that group.
 */
function ProjectVersion({ version, notation }: ProjectVersionProps) {
  const ui = useStores().ui;

  // --- State (one useState per modal group; see grouping note above) ---
  const [addObjectState, setAddObjectState] = useState<AddObjectState>({
    addObjectModalOpen: false,
    newObjectPosition: null,
  });
  const [addInheritanceState, setAddInheritanceState] = useState<AddInheritanceState>({
    addInheritanceModalOpen: false,
    newInheritanceProperties: null,
  });
  const [addEventModalOpen, setAddEventModalOpen] = useState(false);
  const [addSpecialisedEventState, setAddSpecialisedEventState] = useState<AddSpecialisedEventState>({
    addSpecialisedEventModalOpen: false,
    newSpecialisedEventProperties: null,
  });
  const [addMethodState, setAddMethodState] = useState<AddMethodState>({
    addMethodModalOpen: false,
    newMethodEventType: null,
    newMethodObjectType: null,
  });
  const [addFSMState, setAddFSMState] = useState<AddFSMState>({
    addFSMModalOpen: false,
    newFSMObjectType: null,
    newFSMCopyFrom: null,
    newFSMCalculate: false,
  });
  const [addStateState, setAddStateState] = useState<AddStateState>({
    addStateModalOpen: false,
    newStateProperties: null,
  });
  const [addTransitionState, setAddTransitionState] = useState<AddTransitionState>({
    addTransitionModalOpen: false,
    newTransitionProperties: null,
  });
  const [keyboardShortcutsModalOpen, setKeyboardShortcutsModalOpen] = useState(false);
  const [deleteElementState, setDeleteElementState] = useState<DeleteElementState>({
    deleteElementModalOpen: false,
    deleteElementModalProps: null,
  });
  const [report, setReport] = useState<Report | null>(null);

  // Lazy, stable DeleteAnyController bound to this version. The class lazily
  // created it (the `deleteAnyController` getter) and cleared it in
  // componentWillUnmount; because ProjectVersion is remounted per version by its
  // parent, a per-mount useController is behaviour-equivalent (a new instance is
  // created for each version, just as the cleared cache forced a fresh one).
  // The store `IModelVersion` is the same loaded node the controller's narrower
  // `IModelVersion` describes; cast at the construction boundary only (same
  // pattern as the committed forms).
  const deleteAnyController = useController(
    () => new DeleteAnyController(version as unknown as IControllerModelVersion)
  );

  // --- Getters ---

  const inspectorDirty = ui.inspector.dirty;

  const setBusy = useCallback(
    (busy: boolean) => {
      ui.project.setBusy(busy);
    },
    [ui]
  );

  // --- Handlers ---
  //
  // Handlers used as keyboard-listener targets and as confirmable wrappers must
  // keep a stable identity across renders so that componentWillUnmount-style
  // cleanup removes the exact same function reference that was registered. Every
  // handler is therefore wrapped in useCallback. The confirmable wrappers
  // (below) depend on these stable bases.

  const onShowFSM = useCallback(
    (id: string) => {
      ui.visible.showFSM(id);
    },
    [ui]
  );

  // The EDG "Save image" button. Replaces the old EDG's injected
  // `onExportImage: () => ui.project.setExportEDGImage(true)` — ProjectActions
  // consumes the flag to open ExportImageModal (PNG export is 6e scope).
  const onExportEDGImage = useCallback(() => {
    ui.project.setExportEDGImage(true);
  }, [ui]);

  const closeAddObjectModal = useCallback(() => {
    setAddObjectState({
      addObjectModalOpen: false,
      newObjectPosition: null,
    });
  }, []);

  const closeAddInheritanceModal = useCallback(() => {
    setAddInheritanceState({
      addInheritanceModalOpen: false,
      newInheritanceProperties: null,
    });
  }, []);

  const closeAddEventModal = useCallback(() => {
    setAddEventModalOpen(false);
  }, []);

  const closeAddSpecialisedEventModal = useCallback(() => {
    setAddSpecialisedEventState({
      addSpecialisedEventModalOpen: false,
      newSpecialisedEventProperties: null,
    });
  }, []);

  const closeAddMethodModal = useCallback(() => {
    setAddMethodState({
      addMethodModalOpen: false,
      newMethodEventType: null,
      newMethodObjectType: null,
    });
  }, []);

  const closeAddFSMModal = useCallback(() => {
    setAddFSMState({
      addFSMModalOpen: false,
      newFSMObjectType: null,
      newFSMCopyFrom: null,
      newFSMCalculate: false,
    });
  }, []);

  const closeAddStateModal = useCallback(() => {
    setAddStateState({
      addStateModalOpen: false,
      newStateProperties: null,
    });
  }, []);

  const closeAddTransitionModal = useCallback(() => {
    setAddTransitionState({
      addTransitionModalOpen: false,
      newTransitionProperties: null,
    });
  }, []);

  const closeReportModal = useCallback(() => {
    setReport(null);
  }, []);

  const closeDeleteElementModal = useCallback(() => {
    setDeleteElementState({
      deleteElementModalOpen: false,
      deleteElementModalProps: null,
    });
  }, []);

  const openKeyboardShortcutsModal = useCallback(() => {
    setKeyboardShortcutsModalOpen(true);
  }, []);

  const closeKeyboardShortcutsModal = useCallback(() => {
    setKeyboardShortcutsModalOpen(false);
  }, []);

  // hideInspector is also exposed as a confirmable wrapper below.
  const hideInspectorBase = useCallback(() => {
    ui.visible.hideInspector();
  }, [ui]);

  const performDelete = useCallback(
    async (id: string, ...rest: unknown[]) => {
      const controller = deleteAnyController;
      setBusy(true);
      try {
        const result = await controller.deleteAny(id, ...rest);
        closeDeleteElementModal();
        if (result != null && !result.successful) {
          setReport(result);
        } else {
          hideInspectorBase();
        }
      } catch (error) {
        closeDeleteElementModal();
        setReport(unexpectedErrorReport(error));
      } finally {
        setBusy(false);
      }
    },
    [deleteAnyController, setBusy, closeDeleteElementModal, hideInspectorBase]
  );

  const onDelete = useCallback(
    async (id: string | null) => {
      if (id == null) {
        return;
      }
      const controller = deleteAnyController;
      if (!controller.canDelete(id)) {
        return;
      }
      if (controller.isObjectType(id)) {
        setDeleteElementState({
          deleteElementModalOpen: true,
          deleteElementModalProps: {
            id,
            type: 'Object',
          },
        });
      } else if (controller.isInheritanceWithSpecialisedEvents(id)) {
        setDeleteElementState({
          deleteElementModalOpen: true,
          deleteElementModalProps: {
            id,
            type: 'Inheritance',
          },
        });
      } else {
        await performDelete(id);
      }
    },
    [deleteAnyController, performDelete]
  );

  const onRemoveBase = useCallback(
    async (id: string) => {
      await onDelete(id);
    },
    [onDelete]
  );

  const onRemoveSelectedBase = useCallback(async () => {
    await onDelete(ui.selected);
  }, [onDelete, ui]);

  const onAddObjectBase = useCallback((position: Position) => {
    setAddObjectState({
      addObjectModalOpen: true,
      newObjectPosition: position,
    });
  }, []);

  const onAddDependencyBase = useCallback(
    async (from: string, to: string, type: string) => {
      // A fresh controller per invocation, matching the class which did
      // `new AddDependencyController(this.props.version)` on every call. The
      // store `IModelVersion` is the same loaded node the controller's narrower
      // `IModelVersion` describes; cast at the construction boundary only.
      const controller = new AddDependencyController(
        version as unknown as IControllerModelVersion
      );
      setBusy(true);
      try {
        // EdgPanel forwards `type` as a plain `string`; the EDG only ever emits a
        // valid dependency-type literal here, so narrow at the call site.
        const result = await controller.addDependency(from, to, type as DependencyType);
        if (!result.successful) {
          setReport(result);
        }
      } catch (error) {
        setReport(unexpectedErrorReport(error));
      } finally {
        setBusy(false);
      }
    },
    [version, setBusy]
  );

  const onAddInheritanceBase = useCallback((from: string, to: string) => {
    setAddInheritanceState({
      addInheritanceModalOpen: true,
      newInheritanceProperties: {
        supertype: from,
        subtype: to,
      },
    });
  }, []);

  const onAddEventBase = useCallback(() => {
    setAddEventModalOpen(true);
  }, []);

  const onAddSpecialisedEventBase = useCallback((eventType: string) => {
    setAddSpecialisedEventState({
      addSpecialisedEventModalOpen: true,
      newSpecialisedEventProperties: {
        ownerEventType: eventType,
      },
    });
  }, []);

  const onAddMethodBase = useCallback((eventType: string, objectType: string) => {
    setAddMethodState({
      addMethodModalOpen: true,
      newMethodEventType: eventType,
      newMethodObjectType: objectType,
    });
  }, []);

  const onAddFSMBase = useCallback((objectType: string) => {
    setAddFSMState({
      addFSMModalOpen: true,
      newFSMObjectType: objectType,
      newFSMCopyFrom: null,
      newFSMCalculate: false,
    });
  }, []);

  const onCopyFSMBase = useCallback((objectType: string, original: string) => {
    setAddFSMState({
      addFSMModalOpen: true,
      newFSMObjectType: objectType,
      newFSMCopyFrom: original,
      newFSMCalculate: false,
    });
  }, []);

  const onCalculateFSMBase = useCallback((objectType: string) => {
    setAddFSMState({
      addFSMModalOpen: true,
      newFSMObjectType: objectType,
      newFSMCopyFrom: null,
      newFSMCalculate: true,
    });
  }, []);

  // Select an FSM for code generation directly from the FSM window's toolbar —
  // the same controller path the FSM inspector's "Select for code generation"
  // button uses (EditFSMController.selectFSMForCodeGeneration → CEMC_SelectFSM,
  // which also deselects the object type's previously selected FSM).
  const onSelectForCodeGenerationBase = useCallback(
    async (objectType: string, fsm: string) => {
      const controller = new EditFSMController(version as unknown as IControllerModelVersion);
      setBusy(true);
      try {
        const result = await controller.selectFSMForCodeGeneration(objectType, fsm);
        if (!result.successful) {
          setReport(result);
        }
      } catch (error) {
        setReport(unexpectedErrorReport(error));
      } finally {
        setBusy(false);
      }
    },
    [version, setBusy]
  );

  // FsmPanel forwards element *ids* (`fsm.objectType.id`, `fsm.id`); the add-state
  // form declares `objectType`/`fsm` as the concrete interfaces and re-casts them
  // back to a `string` id at its own controller boundary. Cast the id→interface
  // here so the spread into the form types — same id flows through at runtime.
  const onAddStateBase = useCallback(
    (objectType: string, fsm: string, type: StateType, position: Position) => {
      setAddStateState({
        addStateModalOpen: true,
        newStateProperties: {
          type,
          position,
          objectType: objectType as unknown as IObjectType,
          fsm: fsm as unknown as IFSM,
        },
      });
    },
    []
  );

  const onAddTransitionBase = useCallback(
    (objectType: string, fsm: string, source: string, target: string) => {
      setAddTransitionState({
        addTransitionModalOpen: true,
        newTransitionProperties: {
          // Same id→interface cast as add-state (see above).
          objectType: objectType as unknown as IObjectType,
          fsm: fsm as unknown as IFSM,
          source,
          target,
        },
      });
    },
    []
  );

  const onDeselectBase = useCallback(() => {
    ui.select(null, false);
  }, [ui]);

  const onSelectBase = useCallback(
    (id: string | null, toggle = true, openInspector = true) => {
      ui.select(id, toggle, openInspector);
      // `getObjectTypeById` expects a `string`; a null id (deselect) returns
      // undefined, matching the legacy lookup. `content` is loaded here.
      const ot = version.content!.metamodel.getObjectTypeById(id as string);
      if (ot != null && ot.fsmForCodeGeneration != null) {
        onShowFSM(ot.fsmForCodeGeneration.id);
      }
    },
    [ui, version, onShowFSM]
  );

  const onUndoBase = useCallback(() => {
    version.undo();
  }, [version]);

  const onRedoBase = useCallback(() => {
    version.redo();
  }, [version]);

  /**
   * Wrap a handler to first show a confirmation dialog if the inspector is
   * dirty. Only execute the handler if the confirmation was given.
   *
   * The dirty check reads `ui.inspector.dirty` live at call time (not the render
   * snapshot), exactly as the class getter did. On confirm, the discard-guard
   * order is preserved precisely: hideInspector() then inspector.discardChanges()
   * BEFORE the wrapped handler runs (so any selection mutation in the handler
   * happens after the inspector has been discarded).
   */
  // Typed as a generic `Confirmable` so each wrapper preserves its base
  // handler's own argument tuple. `useCallback` can't express a generic
  // function, so the impl is written against the erased `unknown[]` shape and
  // re-typed to `Confirmable` at the boundary (runtime-neutral: it forwards
  // `...args` untouched).
  const confirmable = useCallback(
    ((action: string, handler: (...args: unknown[]) => void) => {
      return (...args: unknown[]) => {
        if (ui.inspector.dirty) {
          // temporarily disable keyboard shortcuts to allow esc/enter to work
          // for the confirmation dialog
          keyboard.disable();
          confirm(action)
            .then(
              () => {
                ui.visible.hideInspector();
                ui.inspector.discardChanges();
                handler(...args);
              },
              () => {
                // ILB
              }
            )
            .then(() => {
              // re-enable the keyboard shortcuts.
              keyboard.enable();
            });
        } else {
          handler(...args);
        }
      };
    }) as Confirmable,
    [ui]
  );

  // Confirmable wrappers. These mirror the constructor reassignments in the
  // class (`this.onAddObject = this.confirmable('Add object', this.onAddObject)`
  // etc.). useMemo keeps each wrapper's identity stable so the keyboard
  // listeners register/unregister the same reference, and so children that
  // memoise on prop identity see no churn.
  const onAddObject = useStableConfirmable(confirmable, 'Add object', onAddObjectBase);
  const onRemoveSelected = useStableConfirmable(
    confirmable,
    'Remove selected',
    onRemoveSelectedBase
  );
  const onRemove = useStableConfirmable(confirmable, 'Remove element', onRemoveBase);
  const onAddDependency = useStableConfirmable(confirmable, 'Add dependency', onAddDependencyBase);
  const onAddInheritance = useStableConfirmable(
    confirmable,
    'Add inheritance relation',
    onAddInheritanceBase
  );
  const onAddEvent = useStableConfirmable(confirmable, 'Add event', onAddEventBase);
  const onAddSpecialisedEvent = useStableConfirmable(
    confirmable,
    'Add specialised event',
    onAddSpecialisedEventBase
  );
  const onAddMethod = useStableConfirmable(confirmable, 'Add method', onAddMethodBase);
  const onAddFSM = useStableConfirmable(confirmable, 'Add FSM', onAddFSMBase);
  const onCopyFSM = useStableConfirmable(confirmable, 'Copy FSM', onCopyFSMBase);
  const onCalculateFSM = useStableConfirmable(confirmable, 'Calculate FSM', onCalculateFSMBase);
  const onSelectForCodeGeneration = useStableConfirmable(
    confirmable,
    'Select for code generation',
    onSelectForCodeGenerationBase
  );
  const onAddState = useStableConfirmable(confirmable, 'Add state', onAddStateBase);
  const onAddTransition = useStableConfirmable(confirmable, 'Add transition', onAddTransitionBase);
  const onSelect = useStableConfirmable(confirmable, 'Select', onSelectBase);
  const onDeselect = useStableConfirmable(confirmable, 'Deselect', onDeselectBase);
  const onUndo = useStableConfirmable(confirmable, 'Undo', onUndoBase);
  const onRedo = useStableConfirmable(confirmable, 'Redo', onRedoBase);
  const hideInspector = useStableConfirmable(confirmable, 'Close inspector', hideInspectorBase);

  // --- Report callbacks (close modal, then surface an unsuccessful report) ---

  const onAddObjectReport = useCallback(
    (result: Report) => {
      closeAddObjectModal();
      if (!result.successful) {
        setReport(result);
      }
    },
    [closeAddObjectModal]
  );

  const onAddInheritanceReport = useCallback(
    (result: Report) => {
      closeAddInheritanceModal();
      if (!result.successful) {
        setReport(result);
      }
    },
    [closeAddInheritanceModal]
  );

  const onAddEventReport = useCallback(
    (result: Report) => {
      closeAddEventModal();
      if (!result.successful) {
        setReport(result);
      }
    },
    [closeAddEventModal]
  );

  const onAddSpecialisedEventReport = useCallback(
    (result: Report) => {
      closeAddSpecialisedEventModal();
      if (!result.successful) {
        setReport(result);
      }
    },
    [closeAddSpecialisedEventModal]
  );

  const onAddMethodReport = useCallback(
    (result: Report) => {
      closeAddMethodModal();
      if (!result.successful) {
        setReport(result);
      }
    },
    [closeAddMethodModal]
  );

  const onAddFSMReport = useCallback(
    (result: Report, fsmId: string) => {
      closeAddFSMModal();
      if (!result.successful) {
        setReport(result);
      } else {
        onShowFSM(fsmId);
        onSelect(fsmId, false, true);
      }
    },
    [closeAddFSMModal, onShowFSM, onSelect]
  );

  const onAddStateReport = useCallback(
    (result: Report) => {
      closeAddStateModal();
      if (!result.successful) {
        setReport(result);
      }
    },
    [closeAddStateModal]
  );

  const onAddTransitionReport = useCallback(
    (result: Report) => {
      closeAddTransitionModal();
      if (!result.successful) {
        setReport(result);
      }
    },
    [closeAddTransitionModal]
  );

  const onInspectorReport = useCallback((result: Report) => {
    if (!result.successful) {
      setReport(result);
    }
  }, []);

  // --- Lifecycle ---

  // beforeunload listener to warn when leaving the page. Reads `version` live
  // via a ref so the listener identity stays stable for the lifetime of the
  // mount (matching the single class instance method) while still seeing the
  // current version's saveState.
  const versionRef = useRef(version);
  versionRef.current = version;

  const confirmClose = useCallback((e: BeforeUnloadEvent) => {
    const currentVersion = versionRef.current;
    if (currentVersion.saveState !== 'idle') {
      const confirmationMessage =
        'Your model is not saved, you may loose data. Are you sure you want to continue?';
      (e || window.event).returnValue = confirmationMessage; // Gecko, Trident, Chrome 34+
      return confirmationMessage; // Gecko, WebKit, Chrome <34
    } else {
      return null;
    }
  }, []);

  // componentDidMount / componentWillUnmount. Empty dep array: runs once on
  // mount, cleans up on unmount, exactly like the class lifecycle. The handlers
  // referenced here (confirmClose, onRemoveSelected, onDeselect,
  // openKeyboardShortcutsModal) are stable across renders, so the same
  // references registered on mount are the ones removed on unmount.
  useEffect(() => {
    // Start autosave
    version.startContentAutosave();
    // Add handler to warn when leaving the page
    window.addEventListener('beforeunload', confirmClose);
    // Add keyboard shortcuts
    keyboard.addListener('del', onRemoveSelected);
    keyboard.addListener('esc', onDeselect);
    keyboard.addListener('F1 h', openKeyboardShortcutsModal);

    return () => {
      // Stop autosave
      version.stopContentAutosave();
      // Clear the selection
      ui.select(null);
      // Remove the beforeunload handler
      window.removeEventListener('beforeunload', confirmClose);
      // Remove the keyboard shortcuts
      keyboard.removeListener('del', onRemoveSelected);
      keyboard.removeListener('esc', onDeselect);
      keyboard.removeListener('F1 h', openKeyboardShortcutsModal);
      // The class also cleared its cached delete-any controller here; with the
      // per-mount useController that cache lives and dies with the component, so
      // there is nothing extra to clear.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Called by <NavigationBlocker> when a navigation is blocked (only happens
  // while the inspector is dirty — see `when={inspectorDirty}`). Shows the same
  // "Discard changes?" confirmation as before; confirming discards the inspector
  // edits and resumes the original navigation via proceed(), cancelling stays
  // put via reset(). proceed() replaces the old confirmedNavigation +
  // history.push(nextLocation) re-navigation dance.
  const handleBlockedNavigation = useCallback(
    (proceed: () => void, reset: () => void) => {
      keyboard.disable();
      confirm('Discard changes?')
        .then(
          () => {
            ui.visible.hideInspector();
            ui.inspector.discardChanges();
            proceed();
          },
          () => {
            reset();
          }
        )
        .then(() => {
          keyboard.enable();
        });
    },
    [ui]
  );

  // --- Rendering ---

  const busy = ui.project.busy;
  const { visible, selected, edg: edgui, oet: oetui, fsm: fsmui } = ui;

  const renderModelExplorer = () => (
    <ModelExplorer
      id={version.id}
      model={version.content}
      requestClose={visible.toggleModelExplorer}
      requestOpen={visible.toggleModelExplorer}
      selected={selected}
      visible={visible.modelExplorer}
      onSelect={(id: string, open: boolean) => onSelect(id, true, open)}
      onShowFSM={visible.showFSM}
    />
  );

  const renderEDG = () => {
    if (visible.edg) {
      return (
        <React.Fragment>
          <EdgPanel
            model={version.content}
            requestClose={visible.toggleEDG}
            onAddObject={onAddObject}
            onAddDependency={onAddDependency}
            onAddInheritance={onAddInheritance}
            // The old EDG selected via `onElementSelected → onSelect(id, false)`
            // (toggle=false); a blank-canvas deselect called `onSelect(null)`
            // (toggle defaults true, irrelevant for null). Preserve that exactly.
            onSelect={(id) => onSelect(id, false)}
            onExportImage={onExportEDGImage}
            ui={edgui}
            selected={selected}
            floatingToolbarClassName={visible.nbPanels > 1 ? '' : 'shifted'}
            notation={notation}
          />
          {(visible.oet || visible.fsms) && <ColumnResizer className="resizer" />}
        </React.Fragment>
      );
    }
  };

  const renderOET = () => {
    if (visible.oet) {
      return (
        <React.Fragment>
          <OET
            id={version.id}
            model={version.content}
            requestClose={visible.toggleOET}
            ui={oetui}
            selected={selected}
            onSelect={onSelect}
            onAddEvent={onAddEvent}
            onAddSpecialisedEvent={onAddSpecialisedEvent}
            onAddMethod={onAddMethod}
            onDelete={onRemove}
          />
          {visible.fsms && <ColumnResizer className="resizer" />}
        </React.Fragment>
      );
    }
  };

  const renderFSMs = () => {
    if (visible.fsms) {
      return (
        <FsmPanel
          model={version.content}
          requestClose={visible.toggleFSMs}
          ui={fsmui}
          selected={selected}
          onSelect={onSelect}
          onAddFSM={onAddFSM}
          onCalculateFSM={onCalculateFSM}
          onCopyFSM={onCopyFSM}
          onSelectForCodeGeneration={onSelectForCodeGeneration}
          onAddState={onAddState}
          onAddTransition={onAddTransition}
        />
      );
    }
  };

  const renderInspector = () => (
    <Inspector
      id={version.id}
      // The Inspector takes the controller-variant `IModelVersion` and the
      // model interface; `version` is the same loaded store node, `content` is
      // non-null whenever the inspector is shown, and `selected` is the legacy
      // nullable id the Inspector's lookups already tolerate. Cast at this
      // boundary only — runtime values are unchanged.
      version={version as unknown as IControllerModelVersion}
      model={version.content as unknown as IModel}
      requestClose={hideInspector}
      requestOpen={visible.showInspector}
      selected={selected as string}
      onReport={onInspectorReport}
      onCopyFSM={onCopyFSM}
      onDelete={onRemove}
      visible={visible.inspector}
    />
  );

  const renderAddObjectModal = () => (
    <Modal isOpen={addObjectState.addObjectModalOpen} onRequestClose={closeAddObjectModal}>
      <AddObjectForm
        modelVersion={version}
        position={addObjectState.newObjectPosition}
        onCancel={closeAddObjectModal}
        onReport={onAddObjectReport}
      />
    </Modal>
  );

  const renderAddInheritanceModal = () => (
    <Modal
      isOpen={addInheritanceState.addInheritanceModalOpen}
      onRequestClose={closeAddInheritanceModal}
    >
      {/* The spread bag is null while the modal is closed — spreading null is an
          empty no-op at runtime, exactly as the legacy JS did. Assert non-null so
          the required form props type; the bag is populated when the modal is
          open. Same pattern for the state/transition spreads below. */}
      <AddInheritanceForm
        modelVersion={version}
        {...(addInheritanceState.newInheritanceProperties as InheritanceProperties)}
        onCancel={closeAddInheritanceModal}
        onReport={onAddInheritanceReport}
      />
    </Modal>
  );

  const renderAddEventModal = () => (
    <Modal isOpen={addEventModalOpen} onRequestClose={closeAddEventModal}>
      <AddEventForm
        modelVersion={version}
        onCancel={closeAddEventModal}
        onReport={onAddEventReport}
      />
    </Modal>
  );

  const renderAddSpecialisedEventModal = () => (
    <Modal
      isOpen={addSpecialisedEventState.addSpecialisedEventModalOpen}
      onRequestClose={closeAddSpecialisedEventModal}
    >
      <AddEventForm
        modelVersion={version}
        {...(addSpecialisedEventState.newSpecialisedEventProperties as SpecialisedEventProperties)}
        onCancel={closeAddSpecialisedEventModal}
        onReport={onAddSpecialisedEventReport}
      />
    </Modal>
  );

  const renderAddMethodModal = () => (
    <Modal isOpen={addMethodState.addMethodModalOpen} onRequestClose={closeAddMethodModal}>
      <AddMethodForm
        modelVersion={version}
        eventType={addMethodState.newMethodEventType as string}
        objectType={addMethodState.newMethodObjectType as string}
        onCancel={closeAddMethodModal}
        onReport={onAddMethodReport}
      />
    </Modal>
  );

  const renderAddFSMModal = () => (
    <Modal isOpen={addFSMState.addFSMModalOpen} onRequestClose={closeAddFSMModal}>
      <AddFSMForm
        modelVersion={version}
        objectType={addFSMState.newFSMObjectType as string}
        copyFrom={addFSMState.newFSMCopyFrom}
        calculate={addFSMState.newFSMCalculate}
        onCancel={closeAddFSMModal}
        onReport={onAddFSMReport}
      />
    </Modal>
  );

  const renderAddStateModal = () => (
    <Modal isOpen={addStateState.addStateModalOpen} onRequestClose={closeAddStateModal}>
      <AddStateForm
        modelVersion={version}
        {...(addStateState.newStateProperties as StateProperties)}
        onCancel={closeAddStateModal}
        onReport={onAddStateReport}
      />
    </Modal>
  );

  const renderAddTransitionModal = () => (
    <AddTransitionModal
      isOpen={addTransitionState.addTransitionModalOpen}
      modelVersion={version}
      {...(addTransitionState.newTransitionProperties as TransitionProperties)}
      onCancel={closeAddTransitionModal}
      onReport={onAddTransitionReport}
    />
  );

  const renderDeleteElementModal = () => {
    const { deleteElementModalOpen, deleteElementModalProps } = deleteElementState;

    return (
      <Modal isOpen={deleteElementModalOpen} onRequestClose={closeDeleteElementModal}>
        {/* `deleteElementModalProps` is non-null whenever the modal is open (the
            `deleteElementModalOpen &&` guard); assert so the reads type without
            changing the runtime guard. */}
        {deleteElementModalOpen && deleteElementModalProps!.type === 'Object' && (
          <DeleteObjectForm
            onCancel={closeDeleteElementModal}
            modelVersion={version}
            id={deleteElementModalProps!.id}
            onPerformDelete={performDelete}
          />
        )}
        {deleteElementModalOpen && deleteElementModalProps!.type === 'Inheritance' && (
          <DeleteInheritanceForm
            onCancel={closeDeleteElementModal}
            modelVersion={version}
            id={deleteElementModalProps!.id}
            onPerformDelete={performDelete}
          />
        )}
      </Modal>
    );
  };

  const renderReportModal = () => (
    <Modal isOpen={report !== null} onRequestClose={closeReportModal} disableBodyScroll={false}>
      {report && (
        <ReportView report={report} onRequestClose={closeReportModal} disableBodyScroll={true} />
      )}
    </Modal>
  );

  const renderKeyboardShortcutsModal = () => (
    <Modal isOpen={keyboardShortcutsModalOpen} onRequestClose={closeKeyboardShortcutsModal}>
      <KeyboardShortcuts onRequestClose={closeKeyboardShortcutsModal} />
    </Modal>
  );

  /**
   * Render the floating toolbar for undo/redo
   */
  const renderUndoRedoToolbar = () => {
    const buttons = [
      {
        key: 'undo',
        icon: Undo,
        handler: onUndo,
        title: 'Undo',
        disabled: !version.canUndo,
      },
      {
        key: 'redo',
        icon: Redo,
        handler: onRedo,
        title: 'Redo',
        disabled: !version.canRedo,
      },
      {
        key: 'shortcuts',
        icon: Keyboard,
        handler: openKeyboardShortcutsModal,
        title: 'Show keyboard shortcuts',
      },
    ];
    return <Toolbar buttons={buttons} variant="floating" />;
  };

  return (
    <div className="project relative flex h-full w-full items-stretch justify-between p-[15px]">
      {renderModelExplorer()}
      <div className="main-views-wrapper relative flex h-full w-[300px] flex-grow items-stretch justify-between mr-[15px]">
        {renderUndoRedoToolbar()}
        {renderEDG()}
        {renderOET()}
        {renderFSMs()}
      </div>
      {renderInspector()}
      {renderAddObjectModal()}
      {renderAddInheritanceModal()}
      {renderAddEventModal()}
      {renderAddSpecialisedEventModal()}
      {renderAddMethodModal()}
      {renderAddFSMModal()}
      {renderAddStateModal()}
      {renderAddTransitionModal()}
      {renderDeleteElementModal()}
      {renderReportModal()}
      {renderKeyboardShortcutsModal()}
      <NavigationBlocker when={inspectorDirty} onBlock={handleBlockedNavigation} />
      <BusyModal isOpen={busy} />
    </div>
  );
}

/**
 * Build a confirmable wrapper with a stable identity. The wrapper is rebuilt
 * only when its `confirmable` factory or the wrapped `handler` change identity;
 * since both are useCallback-stable, the resulting wrapper is stable across
 * renders. This mirrors the class, which built each confirmable wrapper once in
 * the constructor.
 */
function useStableConfirmable<A extends unknown[]>(
  confirmable: Confirmable,
  action: string,
  handler: (...args: A) => void
): (...args: A) => void {
  return React.useMemo(() => confirmable(action, handler), [confirmable, action, handler]);
}

const ObservedProjectVersion = observer(ProjectVersion);
export default ObservedProjectVersion;
